/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Toast } from 'sharedUi/Toast';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useDebounce } from '../../../hooks/useDebounce';
import { sortRows } from '../../../utils/client-sort';
import {
  assignmentsApi,
  roleBindingsApi,
  rolesApi,
  usersApi,
  permissionSetsApi,
} from '../../../services/rbac.service';
import { unitsService } from '../../../services/units.service';
import { projectsService, type AccessibleProject } from '../../../services/projects.service';
import type {
  Assignment,
  AssignmentCreateRequest,
  AssignmentUpdateRequest,
  RbacStatus,
} from '../../../types/rbac';
import DeleteConfirmDialog from '../../../components/dialogs/delete-confirm-dialog';
import EntityLinksDialog, { LinkCard, LinkChips } from '../../../components/rbac/entity-links-dialog';
import ProjectCell from '../../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../../components/common/saas-data-table';
import {
  StatusTag,
  useRbacToast,
  Field,
  TextAreaField,
  DetailShell,
  DetailCard,
  Chips,
  FormShell,
} from './shared';
import { useHasPermission } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';

// Filterable fields — used to seed the default filter state. Assignments store
// userId/roleBindingId as ObjectIds, so description is the only text column.
const FILTER_DEFS: SaasColumn[] = [{ field: 'description', header: 'Description', filter: true }];

interface FormState {
  userId: string;
  roleBindingId: string;
  description: string;
  status: RbacStatus;
}

const EMPTY: FormState = {
  userId: '',
  roleBindingId: '',
  description: '',
  status: 'ACTIVE',
};

// A resolved role binding: role key/name + unit name (or "Global" when unscoped).
interface BindingParts {
  role: string;
  unit: string;
}

// ---------------------------------------------------------------------------
// View dialog helpers (replicate the reference AssignmentViewDialog layout)
// ---------------------------------------------------------------------------
const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
      {label}
    </span>
    <span className="text-right text-sm text-gray-800 dark:text-gray-100">{children}</span>
  </div>
);

const SectionCard = ({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-gray-700">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300">
        <i className={icon} />
      </div>
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
    </div>
    <div className="divide-y divide-gray-100 px-4 py-2 dark:divide-gray-700">{children}</div>
  </div>
);

const formatDate = (value?: string | null) => formatTimestamp(value);

const UserAccessTab = () => {
  const queryClient = useQueryClient();
  const { toast, showSuccess, showError } = useRbacToast();
  const canCreate = useHasPermission(PERMISSIONS.AUTH_USER_ROLE_ASSIGNMENT_CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.AUTH_USER_ROLE_ASSIGNMENT_UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.AUTH_USER_ROLE_ASSIGNMENT_DELETE);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // In-page (client-side) sort of the currently-loaded page. Search + filters stay server-side.
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  // Dialog-only project scope: filters the User + Role-Binding pickers. The
  // assignment's real appId is denormalized server-side from the binding. null = Global.
  // Project scope of the assignment. Three states are meaningful:
  //   undefined -> nothing chosen yet (invalid: Project is mandatory)
  //   null      -> "Global (all projects)" explicitly chosen (a valid choice)
  //   string    -> a specific project
  const [formProjectId, setFormProjectId] = useState<string | null | undefined>(undefined);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewRow, setViewRow] = useState<Assignment | null>(null);
  const [linksRow, setLinksRow] = useState<Assignment | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showGlobal, setShowGlobal] = useState(false);

  const page = Math.floor(first / pageSize) + 1;
  const filtersParam = buildColumnFiltersParam(filters);
  const appIdParam = selectedProjectId || undefined;

  // Projects the caller may access — drives the project filter (shown when >1).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['rbac', 'assignments', debouncedSearch, page, pageSize, filtersParam, appIdParam, showGlobal],
    queryFn: () =>
      assignmentsApi.list({
        search: debouncedSearch || undefined,
        page,
        page_size: pageSize,
        filters: filtersParam,
        app_id: appIdParam,
        include_global: showGlobal,
      }),
  });

  const handleFilter = (event: any) => {
    setFilters(event.filters || {});
    setFirst(0);
  };

  const { data: allUsers = [] } = useQuery({
    queryKey: ['rbac', 'users', 'all'],
    queryFn: () => usersApi.list(),
  });
  const { data: allBindings = [] } = useQuery({
    queryKey: ['rbac', 'role-bindings', 'all'],
    queryFn: () => roleBindingsApi.listAll(),
  });
  const { data: allRoles = [] } = useQuery({
    queryKey: ['rbac', 'roles', 'all'],
    queryFn: () => rolesApi.listAll(),
  });
  const { data: allUnits = [] } = useQuery({
    queryKey: ['rbac', 'units', 'all'],
    queryFn: () => unitsService.getAllForDropdown(),
  });
  // Permission sets drive the resolved-permissions preview for the selected binding.
  const { data: allSets = [] } = useQuery({
    queryKey: ['rbac', 'permission-sets', 'all'],
    queryFn: () => permissionSetsApi.listAll(),
  });

  const roleById = useMemo(() => {
    const m = new Map<string, (typeof allRoles)[number]>();
    allRoles.forEach((r) => m.set(r._id, r));
    return m;
  }, [allRoles]);
  const unitById = useMemo(() => {
    const m = new Map<string, (typeof allUnits)[number]>();
    allUnits.forEach((u) => m.set(u._id, u));
    return m;
  }, [allUnits]);
  const userById = useMemo(() => {
    const m = new Map<string, (typeof allUsers)[number]>();
    allUsers.forEach((u) => m.set(u._id, u));
    return m;
  }, [allUsers]);
  const bindingById = useMemo(() => {
    const m = new Map<string, (typeof allBindings)[number]>();
    allBindings.forEach((b) => m.set(b._id, b));
    return m;
  }, [allBindings]);
  // Permission sets keyed by _id AND by key (roles hold set keys, localAdd holds set _ids).
  const setById = useMemo(() => {
    const m = new Map<string, (typeof allSets)[number]>();
    allSets.forEach((s) => m.set(s._id, s));
    return m;
  }, [allSets]);
  const setByKey = useMemo(() => {
    const m = new Map<string, (typeof allSets)[number]>();
    allSets.forEach((s) => m.set(s.key, s));
    return m;
  }, [allSets]);

  // Effective permissions a binding grants: role default sets (keys) expanded to
  // permission keys, plus the binding's localAdd sets, minus its localRemove keys.
  const resolvedForBinding = (bindingId: string): string[] => {
    const b = bindingById.get(bindingId);
    if (!b) return [];
    const keys = new Set<string>();
    const role = roleById.get(b.roleId);
    (role?.defaultPermissionSets || []).forEach((setKey) => {
      setByKey.get(setKey)?.permissionKeys.forEach((pk) => keys.add(pk));
    });
    (b.localAdd || []).forEach((setId) => {
      setById.get(setId)?.permissionKeys.forEach((pk) => keys.add(pk));
    });
    (b.localRemove || []).forEach((pk) => keys.delete(pk));
    return Array.from(keys).sort();
  };

  // Resolve a role binding into its Role + Unit parts (populated fields preferred).
  const bindingParts = (bindingId: string): BindingParts => {
    const b = bindingById.get(bindingId);
    if (!b) return { role: bindingId, unit: '—' };
    const role =
      b.role?.key || b.role?.displayName || roleById.get(b.roleId)?.key || b.roleId;
    const unit =
      b.unit?.name || (b.unitId ? unitById.get(b.unitId)?.name || b.unitId : 'Global');
    return { role, unit };
  };

  // Compact "role@unit" form used for dropdown option labels.
  const bindingOptionLabel = (bindingId: string) => {
    const { role, unit } = bindingParts(bindingId);
    return `${role} — ${unit}`;
  };

  const userLabel = (userId: string) => {
    const u = userById.get(userId);
    return u?.userId || u?.email || u?.personnelName || userId;
  };

  // Project NAME lookup (name only, no appCode). null appId = Global.
  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    accessibleProjects.forEach((p: AccessibleProject) => m.set(p._id, p.name));
    return m;
  }, [accessibleProjects]);
  const projLabel = useCallback(
    (appId?: string | null) => (appId ? projectNameById.get(appId) || 'Project' : 'Global'),
    [projectNameById]
  );
  // Only real projects are grantable here — Global scope is intentionally omitted.
  const projectOptions = useMemo(
    () =>
      accessibleProjects.map((p: AccessibleProject) => ({
        label: p.name,
        value: p._id as string | null,
      })),
    [accessibleProjects]
  );

  // Scope by the dialog's project (= the assignment's / binding's project):
  //   - Role Binding: appId EXACTLY this project (the binding stamps the assignment's appId).
  //   - User: for a project → users of that project; for Global → any user (a global
  //     binding may be assigned to anyone; users always carry their own appId).
  const bindingAllowed = useCallback(
    (appId?: string | null) => (appId ?? null) === formProjectId,
    [formProjectId]
  );
  const userAllowed = useCallback(
    (appId?: string | null) => formProjectId === null || appId === formProjectId,
    [formProjectId]
  );

  const userOptions = useMemo(
    () =>
      allUsers
        .filter((u) => userAllowed((u as any).appId))
        .map((u) => ({
          label: `${u.userId || u.email || u.personnelName || u._id} · ${projLabel((u as any).appId)}`,
          value: u._id,
        })),
    [allUsers, userAllowed, projLabel]
  );
  const bindingOptions = useMemo(
    () =>
      allBindings
        .filter((b) => bindingAllowed(b.appId))
        .map((b) => ({ label: `${bindingOptionLabel(b._id)} · ${projLabel(b.appId)}`, value: b._id })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allBindings, bindingAllowed, projLabel, roleById, unitById]
  );

  const rows = data?.data || [];
  const displayRows = useMemo(() => sortRows(rows, sortField, sortOrder), [rows, sortField, sortOrder]);
  const total = data?.pagination?.totalItems ?? (data?.pagination as any)?.total ?? 0;

  const setField = (k: keyof FormState, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    // Prefill from the list's active project filter; otherwise leave unselected so
    // the mandatory Project has to be chosen (Global included as an explicit pick).
    setFormProjectId(selectedProjectId ?? undefined);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row: Assignment) => {
    setEditId(row._id);
    setForm({
      userId: row.userId || '',
      roleBindingId: row.roleBindingId || '',
      description: row.description || '',
      status: row.status || 'ACTIVE',
    });
    // Assignment's project is stored on the row (denormalized from its binding).
    setFormProjectId((row.appId as string) ?? null);
    setErrors({});
    setDialogOpen(true);
  };

  // Selecting a Role Binding aligns the dialog's project to that binding's project
  // so the User list narrows to the matching project.
  const onBindingChange = (bindingId: string) => {
    setField('roleBindingId', bindingId);
    const b = bindingById.get(bindingId);
    const bAppId = (b?.appId as string) ?? null;
    if (bAppId !== formProjectId) setFormProjectId(bAppId);
  };

  // When the project changes, drop a User / Role Binding that no longer matches it.
  useEffect(() => {
    if (form.userId) {
      const u = userById.get(form.userId) as any;
      if (u && !userAllowed(u.appId)) setField('userId', '');
    }
    if (form.roleBindingId) {
      const b = bindingById.get(form.roleBindingId);
      if (b && !bindingAllowed(b.appId)) setField('roleBindingId', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formProjectId]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['rbac', 'assignments'] });

  const createMut = useMutation({
    mutationFn: (payload: AssignmentCreateRequest) => assignmentsApi.create(payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Assignment created');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to create assignment'),
  });

  const updateMut = useMutation({
    mutationFn: (payload: AssignmentUpdateRequest) => assignmentsApi.update(editId!, payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Assignment updated');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to update assignment'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => assignmentsApi.remove(id),
    onSuccess: () => {
      invalidate();
      showSuccess('Assignment deleted');
      setDeleteId(null);
    },
    onError: (e) => {
      showError(e, 'Failed to delete assignment');
      setDeleteId(null);
    },
  });

  const saving = createMut.isPending || updateMut.isPending;

  const validate = () => {
    const err: Record<string, string> = {};
    // Project is mandatory — a specific project OR Global (null), but not "unchosen".
    if (formProjectId === undefined) err.project = 'Project is required';
    if (!form.userId) err.userId = 'User is required';
    if (!form.roleBindingId) err.roleBindingId = 'Role binding is required';
    // Project consistency: a project-scoped binding can only be granted to a user
    // of that same project (a global binding may be granted to any user).
    if (form.userId && form.roleBindingId) {
      const u = userById.get(form.userId) as any;
      const b = bindingById.get(form.roleBindingId);
      const bApp = (b?.appId as string) ?? null;
      const uApp = (u?.appId as string) ?? null;
      if (bApp !== null && uApp !== bApp) {
        err.userId = `User must belong to the same project (${projLabel(bApp)}) as the role binding.`;
      }
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const save = () => {
    if (!validate()) return;
    const payload: AssignmentCreateRequest = {
      userId: form.userId,
      roleBindingId: form.roleBindingId,
      description: form.description || undefined,
      status: form.status,
    };
    if (editId) updateMut.mutate(payload);
    else createMut.mutate(payload);
  };

  const columns: SaasColumn<Assignment>[] = useMemo(
    () => [
      {
        field: 'user',
        header: 'USER',
        hideable: false,
        body: (r) => {
          const u = userById.get(r.userId);
          return (
            <div className="leading-tight">
              <div className="font-semibold text-ink">{userLabel(r.userId)}</div>
              {u?.email && u.email !== userLabel(r.userId) && (
                <div className="mt-0.5 text-xs text-ink-muted">{u.email}</div>
              )}
            </div>
          );
        },
      },
      {
        field: 'roleBinding',
        header: 'ROLE BINDING',
        body: (r) => {
          const { role, unit } = bindingParts(r.roleBindingId);
          return (
            <div className="leading-tight">
              <div className="font-mono text-xs text-ink">{role}</div>
              <span className="mt-0.5 inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-gray-700 dark:text-gray-300">
                {unit}
              </span>
            </div>
          );
        },
      },
      {
        field: 'appId',
        header: 'PROJECT',
        body: (r) => <ProjectCell appId={r.appId} projects={accessibleProjects} />,
      },
      {
        field: 'actions',
        header: 'ACTIONS',
        align: 'center',
        hideable: false,
        body: (r) => (
          <div className="flex justify-center gap-1">
            <button
              className="rounded p-2 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              onClick={() => setViewRow(r)}
              aria-label="View"
              title="View"
            >
              <i className="pi pi-eye text-emerald-600" />
            </button>
            <button
              className="rounded p-2 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              onClick={() => setLinksRow(r)}
              aria-label="Links"
              title="Linked entities"
            >
              <i className="pi pi-sitemap text-indigo-600" />
            </button>
            {canUpdate && (
              <button
                className="rounded p-2 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => openEdit(r)}
                aria-label="Edit"
                title="Edit"
              >
                <i className="pi pi-pencil text-blue-600" />
              </button>
            )}
            {canDelete && (
              <button
                className="rounded p-2 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => setDeleteId(r._id)}
                aria-label="Delete"
                title="Delete"
              >
                <i className="pi pi-trash text-red-600" />
              </button>
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userById, roleById, unitById, bindingById, accessibleProjects]
  );

  const viewParts = viewRow ? bindingParts(viewRow.roleBindingId) : null;
  const viewUser = viewRow ? userById.get(viewRow.userId) : null;
  const grantedKeys = form.roleBindingId ? resolvedForBinding(form.roleBindingId) : [];

  // Full-page detail view (replaces the old dialog).
  if (viewRow && viewParts) {
    const resolved = resolvedForBinding(viewRow.roleBindingId);
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <DetailShell
          backLabel="Back to User Access"
          onBack={() => setViewRow(null)}
          title={userLabel(viewRow.userId)}
          subtitle={`${viewParts.role} @ ${viewParts.unit}`}
          actions={canUpdate ? <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => { const r = viewRow; setViewRow(null); openEdit(r); }} /> : undefined}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <SectionCard icon="pi pi-user" title="User">
              <InfoRow label="User ID"><span className="font-mono">{userLabel(viewRow.userId)}</span></InfoRow>
              <InfoRow label="Email">{viewUser?.email || '—'}</InfoRow>
              <InfoRow label="Name">{viewUser?.personnelName || '—'}</InfoRow>
            </SectionCard>
            <SectionCard icon="pi pi-id-card" title="Role Binding">
              <InfoRow label="Role"><span className="font-mono">{viewParts.role}</span></InfoRow>
              <InfoRow label="Unit">{viewParts.unit}</InfoRow>
              <InfoRow label="Status"><StatusTag status={viewRow.status} /></InfoRow>
            </SectionCard>
            <SectionCard icon="pi pi-info-circle" title="Details">
              <InfoRow label="Description">{viewRow.description || '—'}</InfoRow>
            </SectionCard>
            <SectionCard icon="pi pi-clock" title="Audit">
              <InfoRow label="Created">{formatDate((viewRow as any).createdAt)}</InfoRow>
              <InfoRow label="Updated">{formatDate((viewRow as any).updatedAt)}</InfoRow>
            </SectionCard>
          </div>
          <div className="mt-3">
            <DetailCard title="Resolved permissions" count={resolved.length}>
              <div className="max-h-72 overflow-auto"><Chips items={resolved} tone="emerald" /></div>
            </DetailCard>
          </div>
        </DetailShell>
      </>
    );
  }

  // Full-page create / edit form (replaces the old dialog).
  if (dialogOpen) {
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <FormShell
          backLabel="Back to User Access"
          onBack={() => !saving && setDialogOpen(false)}
          title={editId ? 'Edit User Access' : 'Grant User Access'}
          onCancel={() => setDialogOpen(false)}
          onSave={save}
          saving={saving}
          isEdit={!!editId}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Project scope: filters the User & Role-Binding lists. The assignment's
                real appId is derived from the chosen binding. Immutable on edit. */}
            <Field label="Project" required error={errors.project}>
              <Dropdown
                value={formProjectId}
                options={projectOptions}
                onChange={(e) => setFormProjectId(e.value)}
                placeholder="Select project"
                filter
                disabled={!!editId}
                className="w-full"
              />
            </Field>

            <Field label="User" required error={errors.userId}>
              <Dropdown value={form.userId} options={userOptions} onChange={(e) => setField('userId', e.value)} placeholder="Select user" filter className="w-full" />
            </Field>

            <Field label="Role Binding" required error={errors.roleBindingId} className="md:col-span-2">
              <Dropdown value={form.roleBindingId} options={bindingOptions} onChange={(e) => onBindingChange(e.value)} placeholder="Select role binding" filter className="w-full" />
            </Field>

            {/* Transparency: show exactly which permissions this binding grants the user. */}
            {form.roleBindingId && (
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Permissions this grants ({grantedKeys.length})
                </label>
                <div className="max-h-32 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900/40">
                  {grantedKeys.length === 0 ? (
                    <span className="text-xs text-ink-muted">This binding resolves to no permissions.</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {grantedKeys.map((k) => (
                        <span key={k} className="rounded bg-gray-200 px-2 py-0.5 font-mono text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                          {k}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <Field label="Description" className="md:col-span-2">
              <TextAreaField value={form.description} onChange={(v) => setField('description', v)} placeholder="Optional description" rows={2} />
            </Field>
          </div>
        </FormShell>
      </>
    );
  }

  return (
    <div>
      <Toast ref={toast} position="top-right" />
      <SaasDataTable
        tableId="rbac-user-access"
        columns={columns}
        data={displayRows}
        loading={isFetching}
        dataKey="_id"
        emptyMessage="No user access records found"
        first={first}
        rows={pageSize}
        totalRecords={total}
        onPage={(e: any) => {
          setFirst(e.first);
          setPageSize(e.rows);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={(e: any) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
        filters={filters}
        onFilter={handleFilter}
        globalSearch={search}
        onGlobalSearchChange={(v) => {
          setSearch(v);
          setFirst(0);
        }}
        globalSearchPlaceholder="Search user access..."
        testId="UserAccessList"
        toolbarStart={
          <div className="flex flex-wrap items-center gap-3">
            {accessibleProjects.length > 1 && (
              <Dropdown
                value={selectedProjectId}
                options={[
                  { label: 'All projects', value: null },
                  ...accessibleProjects.map((p: AccessibleProject) => ({
                    label: p.name,
                    value: p._id,
                  })),
                ]}
                onChange={(e: any) => { setSelectedProjectId(e.value ?? null); setFirst(0); }}
                placeholder="All projects"
                style={{ minWidth: '190px' }}
              />
            )}
            <label className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <InputSwitch checked={showGlobal} onChange={(e: any) => { setShowGlobal(!!e.value); setFirst(0); }} />
              Show core
            </label>
          </div>
        }
        toolbarEnd={
          <>
            <span className="whitespace-nowrap text-sm text-ink-muted">
              {total} {total === 1 ? 'record' : 'records'}
            </span>
            <RefreshButton
              onClick={() => queryClient.invalidateQueries({ queryKey: ['rbac', 'assignments'] })}
              loading={isFetching}
            />
            {canCreate && <Button label="Grant Access" icon="pi pi-plus" onClick={openCreate} size="small" />}
          </>
        }
      />

      <DeleteConfirmDialog
        visible={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onDelete={() => deleteId && deleteMut.mutate(deleteId)}
        message="Are you sure you want to revoke this user access?"
        loading={deleteMut.isPending}
      />

      <EntityLinksDialog
        entityId={linksRow?._id ?? null}
        title="User Access Links"
        resourceKey="assignment"
        onHide={() => setLinksRow(null)}
      >
        {linksRow && (
          <>
            <LinkCard icon="pi pi-user" title="User">
              <span className="font-mono text-sm text-ink">
                {userLabel(linksRow.userId)}
              </span>
            </LinkCard>
            <LinkCard icon="pi pi-id-card" title="Role Binding">
              <span className="font-mono text-sm text-ink">
                {bindingParts(linksRow.roleBindingId).role} @ {bindingParts(linksRow.roleBindingId).unit}
              </span>
            </LinkCard>
            <LinkCard
              icon="pi pi-key"
              title="Resolved Permissions"
              count={resolvedForBinding(linksRow.roleBindingId).length}
            >
              <LinkChips items={resolvedForBinding(linksRow.roleBindingId)} tone="emerald" />
            </LinkCard>
          </>
        )}
      </EntityLinksDialog>
    </div>
  );
};

export default UserAccessTab;
